import { POI, School } from "@local-logic/client";
import { TrackingEvents, useTracking } from "@local-logic/core/tracking";
import { scorePois } from "@local-logic/elements-react";
import {
  multiPoint,
  point,
  bbox,
  FeatureCollection,
  Properties,
  GeometryCollection,
} from "@turf/turf";
import React, { useRef, useMemo, useEffect, useState } from "react";
import {
  ViewStateChangeEvent,
  MapRef,
  Layer,
  Source,
  LayerProps,
} from "react-map-gl/maplibre";
import Map from "~/components/MapGL";
import Attribution from "~/components/Attribution";
import CommuteContainer from "~/sdks/LocalContent/components/CommuteContainer";
import DesktopScores from "~/sdks/LocalContent/components/DesktopScores";
import Pin from "~/components/Pin";
import { useStateManagerContext } from "~/context/stateManager";
import useEffectOnce from "~/hooks/useEffectOnce";
import useIsMobile from "~/hooks/useIsMobile";
import useMeasure from "~/hooks/useMeasure";
import errorTracker from "~/utils/errorTracker";
import { LocalContentProps } from "~/sdks/LocalContent/types";
import { useSchools } from "~/hooks/useSchools";
import { usePois } from "~/hooks/usePois";
import { useLocalContent } from "~/sdks/LocalContent/context";
import MobileScores from "~/sdks/LocalContent/components/MobileScores";
import Markers from "./Markers";

const MapGLMap = () => {
  const { sdkState, setSdkState } = useStateManagerContext<LocalContentProps>();
  const {
    activeScore,
    activePOI,
    setActivePOI,
    hoveredPOI,
    commuteGeojson,
    destination,
  } = useLocalContent();
  const isMobile = useIsMobile();

  const poisResult = usePois(activeScore);
  const schoolsResult = useSchools({ activeScore });
  const tracking = useTracking();
  const [isMapMoving, setIsMapMoving] = useState(false);

  const commuteLineColor = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--ll-color-link");
  const roteLayerStyle: LayerProps = {
    id: "point",
    type: "line",
    paint: {
      "line-width": 6,
      "line-color": commuteLineColor,
    },
  };

  useEffect(() => {
    if (activePOI) {
      tracking.postEvent({
        eventType: TrackingEvents.LOCALCONTENT_POI_CLICKED,
        eventAttributes: [
          {
            label: "activePOI",
            data: activePOI?.name,
          },
        ],
      });
    }
  }, [activePOI]);

  useEffect(() => {
    if (typeof activePOI !== "undefined" && isMapMoving) {
      setActivePOI(undefined);
    }
  }, [activePOI, isMapMoving, setActivePOI]);

  const activePOIs = useMemo(() => {
    if (
      (activeScore === "high_schools" || activeScore === "primary_schools") &&
      !schoolsResult.isFetching
    ) {
      return { [activeScore]: schoolsResult?.data?.[activeScore] || [] };
    }

    if (
      !(
        scorePois &&
        activeScore &&
        scorePois[activeScore] &&
        scorePois[activeScore]?.length
      ) &&
      !poisResult.isFetching
    ) {
      return { [activeScore]: [] };
    }

    return poisResult.data ? poisResult.data : [];
  }, [poisResult.data, schoolsResult.data, activeScore]);

  /**
   * POIs to render on the map
   */
  const points = useMemo(() => {
    if (!activePOIs) {
      return [];
    }

    const arr = Object.entries(activePOIs)
      .map(([key, pois]) =>
        (pois as POI[] | School[]).map((poi) =>
          point([poi.lng, poi.lat], { poi, type: key }),
        ),
      )
      .flat();

    return arr.filter(
      (v, i, a) =>
        a.findIndex((v2) =>
          ["lat", "lng", "name"].every(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            (k) => v2.properties.poi[k] === v.properties.poi[k],
          ),
        ) === i,
    );
  }, [activePOIs]);

  /**
   * The POIs to render on the map (including the Pin)
   */
  const pointsWithPin = useMemo(() => {
    const markerPoint =
      sdkState?.options?.marker &&
      point([sdkState?.options?.marker.lng, sdkState?.options?.marker.lat]);

    return [
      ...points,
      ...(markerPoint ? [markerPoint] : []),
      ...(destination ? [point([destination?.lng, destination?.lat])] : []),
    ];
  }, [points, sdkState?.options?.marker, destination]);

  const mapRef = useRef<MapRef | undefined>();
  const [ref, rect] = useMeasure<HTMLDivElement>();

  const mobileMapPadding = useMemo(() => {
    const padding = 75;

    return {
      top: padding,
      bottom: padding,
      left: padding,
      right: padding,
    };
  }, [rect.height]);

  const desktopMapPadding = useMemo(() => {
    const padding = 80;

    return {
      top: padding,
      bottom: padding,
      left: rect.width + 32 + padding,
      right: padding,
    };
  }, [rect.width]);

  const mapPadding = isMobile ? mobileMapPadding : desktopMapPadding;

  const handleOnMove = (e: ViewStateChangeEvent) => {
    setSdkState({
      ...sdkState,
      options: {
        ...sdkState.options,
        lat: e.viewState.latitude,
        lng: e.viewState.longitude,
        zoom: e.viewState.zoom,
        bearing: e.viewState.bearing,
        pitch: e.viewState.pitch,
      },
    });
  };

  /**
   * Reposition map when points change
   */
  useEffect(() => {
    if (
      !activeScore ||
      poisResult.isFetching ||
      schoolsResult.isFetching ||
      !pointsWithPin.length ||
      !sdkState.options?.marker
    ) {
      return;
    }
    const multiPt = multiPoint(
      pointsWithPin.map(({ geometry }) => [
        geometry.coordinates[1],
        geometry.coordinates[0],
      ]),
    );
    const [minLat, minLng, maxLat, maxLng] = bbox(multiPt);

    mapRef?.current?.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        maxZoom: 17,
        padding: mapPadding,
        duration: 1000,
      },
    );
  }, [
    pointsWithPin,
    mapPadding,
    activeScore,
    schoolsResult.isFetching,
    poisResult.isFetching,
  ]);

  const handleAttributionClicked = () => {
    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_ATTRIBUTION_CLICKED,
    });
  };

  useEffectOnce(() => {
    errorTracker.addBreadCrumb("localContent", "Local Content loaded");
    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_LOADED,
    });
  });

  const handleOnMapLoad = () => {
    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_VIEWED,
    });
    errorTracker.addBreadCrumb("localContent", "Map discovered");
  };

  if (!sdkState.options) {
    return <div>No options provided.</div>;
  }

  return (
    <>
      <Map
        mapRef={mapRef}
        mapPadding={mapPadding}
        distanceUnit={sdkState.options.distanceUnit}
        handleOnMove={handleOnMove}
        setIsMapMoving={setIsMapMoving}
        handleOnMapLoad={handleOnMapLoad}
        style={{
          flex: 1,
          width: "100vw",
        }}
        position={isMobile ? "top-right" : "bottom-right"}
        navigationStyle={{
          marginTop: isMobile ? 16 + rect.height : 16,
          marginRight: 16,
        }}
      >
        <>
          {sdkState.options.marker && <Pin marker={sdkState.options.marker} />}
          <Markers
            rect={rect}
            zoom={sdkState.options.zoom!}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            mapRef={mapRef}
            points={points}
            isMobile={isMobile}
            activePOI={activePOI}
            hoveredPOI={hoveredPOI}
            isMapMoving={isMapMoving}
          />
          {isMobile ? <MobileScores ref={ref} /> : <DesktopScores ref={ref} />}

          <CommuteContainer />

          {destination && <Pin marker={{ icon: "pin", ...destination }} />}

          {commuteGeojson && (
            <Source
              type="geojson"
              data={
                commuteGeojson as FeatureCollection<
                  GeometryCollection,
                  Properties
                >
              }
            >
              <Layer {...roteLayerStyle} />
            </Source>
          )}
        </>
      </Map>
      {isMobile && (
        <Attribution attributionClicked={handleAttributionClicked} />
      )}
    </>
  );
};

export default MapGLMap;
